import {
  BackEndValueType,
  ECardinality,
  ESymbolCategory,
  IActionParameter,
  ISymbol,
  IWorkFlow
} from "@kuika/kuika-cl-model";

/**
 * WS1c (process-automation StartProcess wiring) — headless expansion of a
 * `StartProcess` / `StartExternalProcess` action's DYNAMIC workflow INPUT
 * parameters.
 *
 * When a workflow is selected on one of these actions, the designer surfaces
 * that workflow's INPUT business-object parameters as additional bindable
 * action parameters, so the initiator form's inputs can be bound to them.
 * That expansion previously lived ONLY in the React properties panel
 * (`components/design-tools/right-panel/properties-panel/action-panel.tsx`),
 * so the MCP tool handlers could never see or bind those params — a bind
 * attempt failed in `getResolvedActionParameter` ("parameter not found") and
 * an attach-time override was silently dropped.
 *
 * This module is the single source of truth for the "which workflow + its
 * INPUT params" resolution. Its logic is a faithful extraction of the panel's
 * StartProcess / StartExternalProcess blocks so the panel can later consume it
 * without a behavior change.
 */

const WORKFLOW_START_ACTION_LABELS: ReadonlySet<string> = new Set<string>(["StartProcess", "StartExternalProcess"]);

/** Minimal action shape the expansion reads off the live action instance. */
export interface IWorkflowStartActionLike {
  label?: string;
  parameters?: IActionParameter[];
}

/** Minimal project shape the SD instance carries in `projectsWithWFData`. */
export interface IWorkflowStartProjectData {
  id: string;
  workflows: IWorkFlow[];
}

/** Minimal current-project shape the SD instance carries in `project`. */
export interface IWorkflowStartCurrentProject {
  id?: string;
}

/** Structural subset of the SD instance the expansion reads. */
export interface IWorkflowStartExpansionContext {
  project?: IWorkflowStartCurrentProject;
  workFlowList?: IWorkFlow[];
  projectsWithWFData?: IWorkflowStartProjectData[];
  /**
   * The currently-OPEN workflow. `workFlowList` entries are list DTOs whose
   * `parameters` is undefined (the project's workflow-list fetch omits the
   * `json` blob that carries params + nodes — see `loadWorkFlowList`: "WFList
   * does not contain nodes and xml"), so the INPUT-param expansion reads an
   * empty list off them. `selectedWf` is built from a FULL Get and DOES carry
   * the BO INPUT params, so it is the authoritative source when it IS the
   * target workflow (KOA-17362 F1).
   */
  selectedWf?: IWorkFlow;
}

interface IWorkflowProviderState {
  workFlowList?: IWorkFlow[];
  selectedWf?: IWorkFlow;
}

interface IWorkflowProviderLike {
  state?: IWorkflowProviderState;
}

interface IProjectProviderState {
  project?: IWorkflowStartCurrentProject;
}

interface IProjectProviderLike {
  state?: IProjectProviderState;
}

/**
 * The (mostly untyped) SD-instance sub-paths the expansion needs. The workflow
 * lists are not declared on `ScreenDesignContextProvider`'s public type, so the
 * caller adapts the live instance to this shape (matching the defensive access
 * in `workflow-tools.ts` / `designer-workflow-step-actions.ts`), keeping the
 * expansion functions pure and unit-testable against `IWorkflowStartExpansionContext`.
 */
export interface ISdWorkflowStartSource {
  projectProvider?: IProjectProviderLike;
  workflowProvider?: IWorkflowProviderLike;
  state?: IWorkflowProviderState;
  workFlowList?: IWorkFlow[];
  selectedWf?: IWorkFlow;
  projectsWithWFData?: IWorkflowStartProjectData[];
}

/** Adapt a live SD instance to the pure expansion context. */
export function buildWorkflowStartExpansionContext(sd: ISdWorkflowStartSource): IWorkflowStartExpansionContext {
  return {
    project: sd.projectProvider?.state?.project,
    workFlowList: sd.workflowProvider?.state?.workFlowList ?? sd.workFlowList ?? sd.state?.workFlowList,
    selectedWf: sd.workflowProvider?.state?.selectedWf ?? sd.selectedWf ?? sd.state?.selectedWf,
    projectsWithWFData: sd.projectsWithWFData
  };
}

/** True iff the action is one whose params expand from a selected workflow. */
export function isWorkflowStartAction(action: IWorkflowStartActionLike | null | undefined): boolean {
  const label: unknown = action?.label;
  return typeof label === "string" && WORKFLOW_START_ACTION_LABELS.has(label);
}

function readParameterFixedValue(action: IWorkflowStartActionLike, parameterName: string): string | undefined {
  const params: IActionParameter[] = action.parameters ?? [];
  const match: IActionParameter | undefined = params.find((p: IActionParameter): boolean => p.name === parameterName);
  const fixedValue: unknown = match?.symbol?.fixedValue;
  return typeof fixedValue === "string" ? fixedValue : undefined;
}

/**
 * Resolve the workflow selected on a StartProcess / StartExternalProcess action
 * from its `workflow` (process id) + `projectId` params. Faithful to
 * `action-panel.tsx`: a truthy `projectId` equal to the current project selects
 * the current project's `workFlowList`; anything else (incl. empty / undefined
 * / a different project) resolves through `projectsWithWFData`.
 */
export function resolveSelectedWorkflowForStartAction(
  action: IWorkflowStartActionLike | null | undefined,
  ctx: IWorkflowStartExpansionContext
): IWorkFlow | undefined {
  if (!action || !isWorkflowStartAction(action)) return undefined;
  const processId: string | undefined = readParameterFixedValue(action, "workflow");
  if (processId === undefined) return undefined;
  // Prefer the currently-open workflow when it IS the target. `workFlowList`
  // entries are list DTOs without the `json` blob, so their `parameters` is
  // undefined and the INPUT-param expansion below would read an empty list off
  // them — leaving the initiator form's inputs unbound (KOA-17362 F1). Only
  // `selectedWf` (a full Get) carries the BO INPUT params, and in the
  // autonomous compound-workflow build the freshly-created process IS the open
  // one when `applyProcessStartWiring` runs.
  if (ctx.selectedWf && ctx.selectedWf.id === processId) return ctx.selectedWf;
  const projectId: string | undefined = readParameterFixedValue(action, "projectId");
  const currentProjectId: string | undefined = ctx.project?.id;
  const isCurrentProject: boolean = projectId !== undefined && projectId.length > 0 && projectId === currentProjectId;
  if (isCurrentProject) {
    return (ctx.workFlowList ?? []).find((wf: IWorkFlow): boolean => wf.id === processId);
  }
  const project: IWorkflowStartProjectData | undefined = (ctx.projectsWithWFData ?? []).find(
    (p: IWorkflowStartProjectData): boolean => p.id === projectId
  );
  return (project?.workflows ?? []).find((wf: IWorkFlow): boolean => wf.id === processId);
}

/**
 * Build the workflow's INPUT parameters as `IActionParameter[]` — the dynamic
 * params the panel appends to a StartProcess / StartExternalProcess action once
 * a workflow is chosen. Returns `[]` when the action is not a workflow-start
 * action or no workflow is resolvable. Construction mirrors `action-panel.tsx`.
 */
export function expandWorkflowStartInputParameters(
  action: IWorkflowStartActionLike | null | undefined,
  ctx: IWorkflowStartExpansionContext
): IActionParameter[] {
  const workflow: IWorkFlow | undefined = resolveSelectedWorkflowForStartAction(action, ctx);
  if (!workflow) return [];
  return (workflow.parameters ?? [])
    .filter((p): boolean => p.isInput === true)
    .map((p): IActionParameter => {
      // Carry the parameter's real cardinality (a List<T> input must not collapse to One — the workflow
      // data-model property is List<T> and the StartProcess assignment casts accordingly) and, for a
      // complex input, its Kuika Data Type so the request codegen news up the right POCO.
      const isObject: boolean = p.isObject === true && typeof p.dataTypeName === "string";
      const param: IActionParameter = {
        name: p.name,
        symbol: p.defaultValue as ISymbol,
        cardinality: p.cardinality ?? ECardinality.One,
        Typename: isObject ? (p.dataTypeName as BackEndValueType) : p.dataType,
        isObject: isObject || undefined,
        objectClassName: isObject ? p.dataTypeName : undefined
      };
      return param;
    });
}

/**
 * KOA-17362 (F3) — headless expansion of a `ResumeProcess` / `SaveStepData`
 * action's DYNAMIC step-OUTPUT parameters (the approval decision params, e.g.
 * `managerApproved` / `hrApproved`).
 *
 * A managed task screen's ResumeProcess surfaces the workflow step's OUTPUT
 * parameters as bindable action params — but ONLY the React properties panel
 * (`action-panel.tsx` ResumeProcess block, KOA-17362) materializes them, by
 * matching `project.processStepScreenIds[screenId].stepId` → the step's
 * `outputParameters`. The MCP bind path (`getResolvedActionParameter`) reads
 * only the action instance's STATIC `parameters` array, which carries a stale
 * base signature, so a bind against the (real, panel-visible) decision param
 * failed as "parameter not found" — mirroring the StartProcess WS1c gap above.
 *
 * This is the headless equivalent of the panel's expansion, kept pure and
 * testable against the injected source. Its resolution of "which workflow"
 * from a step link mirrors the panel's `getWf`.
 */
const WORKFLOW_RESUME_ACTION_LABELS: ReadonlySet<string> = new Set<string>(["ResumeProcess", "SaveStepData"]);

/** One `project.processStepScreenIds` entry — links a managed screen to its step. */
export interface IProcessStepScreenLink {
  screenId?: string;
  stepId?: string;
  processId?: string;
  projectId?: string;
}

/** Structural subset of a workflow step the resume expansion reads. */
interface IWorkflowStepOutputParamLike {
  id?: string;
  value?: ISymbol;
}
interface IWorkflowStepLike {
  id?: string;
  outputParameters?: IWorkflowStepOutputParamLike[];
}

/** Structural subset of the SD instance the resume expansion reads. */
export interface IWorkflowResumeSource {
  getProject?: () => { processStepScreenIds?: IProcessStepScreenLink[] } | undefined;
  getScreenDesign?: () => { id?: string } | undefined;
  findStepRecursively?: (
    workflow: IWorkFlow,
    condition: (step: IWorkflowStepLike) => boolean
  ) => IWorkflowStepLike | undefined;
  workFlowList?: IWorkFlow[];
  projectsWithWFData?: IWorkflowStartProjectData[];
}

/** True iff the action is one whose params expand from the current screen's step outputs. */
export function isWorkflowResumeAction(action: IWorkflowStartActionLike | null | undefined): boolean {
  const label: unknown = action?.label;
  return typeof label === "string" && WORKFLOW_RESUME_ACTION_LABELS.has(label);
}

/** Resolve the workflow a step link points at — faithful to `action-panel.tsx` `getWf`. */
function resolveWorkflowForStepLink(link: IProcessStepScreenLink, sd: IWorkflowResumeSource): IWorkFlow | undefined {
  const processId: string | undefined = link.processId;
  if (link.projectId !== undefined && link.projectId.length > 0) {
    const project: IWorkflowStartProjectData | undefined = (sd.projectsWithWFData ?? []).find(
      (p: IWorkflowStartProjectData): boolean => p.id === link.projectId
    );
    return (project?.workflows ?? []).find((wf: IWorkFlow): boolean => wf.id === processId);
  }
  return (sd.workFlowList ?? []).find((wf: IWorkFlow): boolean => wf.id === processId);
}

/** Shallow-clone a step output symbol (the Binder overwrites it; a distinct object avoids mutating the workflow param). */
function cloneResumeParamSymbol(value: ISymbol | undefined | null): ISymbol {
  if (value === undefined || value === null) {
    return { currentType: "Boolean", targetType: "Boolean" } as ISymbol;
  }
  return { ...(value as object) } as ISymbol;
}

/**
 * Build the current screen's ResumeProcess / SaveStepData step-OUTPUT parameters
 * as `IActionParameter[]` — the dynamic decision params the panel appends once
 * the managed screen's step is resolved. Returns `[]` when the action is not a
 * resume action, the current screen is not a managed task screen, or the step /
 * workflow cannot be resolved. Construction mirrors `action-panel.tsx`.
 */
export function expandWorkflowResumeOutputParameters(
  action: IWorkflowStartActionLike | null | undefined,
  sd: IWorkflowResumeSource
): IActionParameter[] {
  if (!isWorkflowResumeAction(action)) return [];
  const screenId: string | undefined = sd.getScreenDesign?.()?.id;
  if (screenId === undefined || screenId.length === 0) return [];
  const link: IProcessStepScreenLink | undefined = sd
    .getProject?.()
    ?.processStepScreenIds?.find((s: IProcessStepScreenLink): boolean => s.screenId === screenId);
  if (!link) return [];
  const workflow: IWorkFlow | undefined = resolveWorkflowForStepLink(link, sd);
  if (!workflow) return [];
  const step: IWorkflowStepLike | undefined = sd.findStepRecursively?.(
    workflow,
    (s: IWorkflowStepLike): boolean => s.id === link.stepId
  );
  const outputs: IWorkflowStepOutputParamLike[] = step?.outputParameters ?? [];
  const params: IActionParameter[] = [];
  for (const out of outputs) {
    const wfPrm = (workflow.parameters ?? []).find((p): boolean => p.id === out.id);
    if (!wfPrm || typeof wfPrm.name !== "string" || wfPrm.name.length === 0) continue;
    // Object/list params: surface the referenced Kuika Data Type + cardinality + isObject so the panel
    // can render field-by-field mapping (nested `parameters` populated there from the catalog).
    const isObject: boolean = wfPrm.isObject === true && typeof wfPrm.dataTypeName === "string";
    params.push({
      name: wfPrm.name,
      symbol: cloneResumeParamSymbol(out.value),
      cardinality: wfPrm.cardinality ?? ECardinality.One,
      Typename: isObject ? (wfPrm.dataTypeName as BackEndValueType) : wfPrm.dataType,
      isObject: isObject || undefined
    });
  }
  return params;
}

function buildFixedProjectSymbol(projectId: string): ISymbol {
  return { category: ESymbolCategory.fixed, label: "Project", description: "Project", fixedValue: projectId };
}

/**
 * Default a `StartProcess` action's `projectId` to the CURRENT project id when
 * it is empty/missing — the headless equivalent of the panel's `_fixProjectId`
 * (`workflow-start-symbols-group.tsx`). StartProcess targets the current project
 * by default (the "Start External Process" switch is off on add) and its
 * projectId must never be empty: `""` is classified as an EXTERNAL project
 * (`_checkIsExternalProcess`), and `resolveSelectedWorkflowForStartAction` only
 * finds the workflow in `workFlowList` when `projectId === currentProjectId`.
 * The panel fills this on render; the headless MCP attach path has no panel, so
 * without this a StartProcess attached via `ui_action.add_to_event` keeps an
 * empty projectId and the workflow-INPUT expansion resolves nothing at bind
 * time. Mutates `action.parameters` in place. No-op for non-StartProcess
 * actions (StartExternalProcess is deliberately excluded — it targets a
 * different project) or when the current project id is unavailable.
 */
export function applyDefaultStartProcessProjectId(
  action: IWorkflowStartActionLike | null | undefined,
  sd: ISdWorkflowStartSource
): void {
  if (!action || action.label !== "StartProcess") return;
  const currentProjectId: string | undefined = sd.projectProvider?.state?.project?.id;
  if (currentProjectId === undefined || currentProjectId.length === 0) return;
  if (!Array.isArray(action.parameters)) action.parameters = [];
  const projectIdParam: IActionParameter | undefined = action.parameters.find(
    (p: IActionParameter): boolean => p.name === "projectId"
  );
  const existingFixed: unknown = projectIdParam?.symbol?.fixedValue;
  const isEmpty: boolean = typeof existingFixed !== "string" || existingFixed.length === 0;
  if (!isEmpty) return;
  const symbol: ISymbol = buildFixedProjectSymbol(currentProjectId);
  if (projectIdParam) {
    projectIdParam.symbol = symbol;
  } else {
    action.parameters.push({ name: "projectId", symbol, cardinality: ECardinality.One, Typename: "String" });
  }
}
